import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Loader2, X } from 'lucide-react';

interface DeleteServerModalProps {
  serverName: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const DeleteServerModal: React.FC<DeleteServerModalProps> = ({ serverName, onConfirm, onClose }) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const canDelete = confirmText === serverName;

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      await onConfirm();
    } finally {
      setDeleting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-bg-primary rounded-2xl w-full max-w-md border border-border-color shadow-2xl shadow-black/50 animate-slide-up overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-error/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-error" />
            </div>
            <div>
              <h3 className="text-base font-bold text-text-primary">Delete Server</h3>
              <p className="text-xs text-text-muted">This action is permanent and cannot be undone.</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4 space-y-4">
          <div className="bg-error/5 border border-error/20 rounded-xl p-4">
            <p className="text-xs text-text-secondary">
              This will permanently delete <span className="font-bold text-text-primary">{serverName}</span>, 
              including all channels, messages, and settings. All members will be removed.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              Type <span className="font-bold text-text-primary">{serverName}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="settings-input"
              placeholder={serverName}
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-highlight/50 border border-border-color transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!canDelete || deleting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-error text-white hover:bg-error/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {deleting ? 'Deleting...' : 'Delete Server'}
          </button>
        </div>
      </div>
    </div>
  , document.body);
};

export default DeleteServerModal;
